import React, { useRef } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { useDrag, useDrop } from "react-dnd";
import cx from "classnames";

// used to run some optimizations. Fixes NEST-1299
export const DisplayTypes = {
  GRID: "grid",
  LIST: "list",
};

// track the last item hovered. This way we can avoid a weird flicker issue when an item is tall enough that
// it will still hover with the dragged element after being replaced, replacement of elements will loop too many times
// creating a flickering effect. Fixes NEST-1299
let lastHoveredUUID = null;

const getItemUUID = item => {
  return `${item.type}_${item.id}`;
};

const configureDrop = (item, ref, displayType, onHover = null, canDrop = null) => {
  const dropExtras = {};

  if (canDrop) {
    dropExtras.canDrop = (sourceItem, monitor) => {
      // item represents the target item. Monitor is optional and will have a reference to the sourceItem via
      // monitor.getItem();
      return canDrop(sourceItem, item, monitor);
    };
  }

  return useDrop({
    ...dropExtras,
    accept: item.accept,
    hover(dragItem) {
      if (!onHover) {
        return;
      }

      if (!ref.current) {
        return;
      }

      const dragId = dragItem.id;
      const hoverId = item.id;

      // Don't replace items with themselves
      if (dragId === hoverId) {
        return;
      }

      const currentHoveredUUID = getItemUUID(item);

      // Avoid some flickering when element has been reordered already and the display is in a column (list).
      // Flickering happens when elements are of different height.
      if (displayType === DisplayTypes.LIST && lastHoveredUUID === currentHoveredUUID) {
        return;
      }

      onHover(dragItem, item);

      // Only record the last hovered item uuid if the display type is LIST. Fixes NEST-1299
      if (displayType === DisplayTypes.LIST) {
        lastHoveredUUID = currentHoveredUUID;
      }
    },
    drop: () => item,
    collect: monitor => ({
      isOver: monitor.isOver(),
      acceptingDrop: monitor.canDrop(),
    }),
  });
};

const configureDrag = (item, onDragEnd, canDrag = null, onDragBegin = null) => {
  const dragExtras = {};

  if (canDrag) {
    dragExtras.canDrag = canDrag;
  }

  if (onDragBegin) {
    dragExtras.begin = monitor => onDragBegin(item, monitor);
  }

  const [{ isDragging }, drag] = useDrag({
    ...dragExtras,
    item,
    end: (sourceItem, monitor) => {
      const targetItem = monitor.getDropResult();
      onDragEnd && onDragEnd(sourceItem, targetItem, monitor);
      // when the user releases the dragged element, let's clear the last hovered id.
      lastHoveredUUID = null;
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return [drag, isDragging];
};

const DnDItem = observer(
  ({
    as: Component,
    item,
    className,
    displayType,
    children,
    onClick,
    onHover,
    onDragBegin,
    onDragEnd,
    canDrop,
    canDrag,
    disabled,
    selected,
    draggingItemId,
    ...rest
  }) => {
    const ref = useRef(null);
    const [{ isOver, acceptingDrop }, drop] = configureDrop(item, ref, displayType, onHover, canDrop);
    const [drag, isDragging] = configureDrag(item, onDragEnd, canDrag, onDragBegin);

    drag(drop(ref));

    const classNames = cx(
      className,
      !disabled && {
        selected,
        draggable: true,
        // dragging along means that this item belongs to a group of selected items, and that
        // it should drag along with another item that is being dragged, being the other item where the actual
        // drag event fired.
        "dragging-along": selected && draggingItemId && draggingItemId !== item.id,
        dragging: isDragging && !disabled,
        over: isOver,
        "accepting-drop": acceptingDrop,
      },
    );

    return (
      <Component
        ref={disabled ? undefined : ref}
        className={classNames}
        onClick={event => onClick && onClick(item, event)}
        {...rest}
      >
        {children}
      </Component>
    );
  },
);

DnDItem.propTypes = {
  className: PropTypes.string,
  as: PropTypes.any,
  // Display GRID is the default value. Pass display LIST when elements are arranged in a colum (list).
  // This component will do some optimizations to avoid a flickering effect when moving items up or down when
  // elements are of different heights. Fixes NEST-1299
  displayType: PropTypes.oneOf([DisplayTypes.GRID, DisplayTypes.LIST]),
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    type: PropTypes.string.isRequired,
    accept: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]).isRequired,
  }),
  disabled: PropTypes.bool,
  selected: PropTypes.bool,
  draggingItemId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClick: PropTypes.func,
  onHover: PropTypes.func,
  onDragBegin: PropTypes.func,
  onDragEnd: PropTypes.func,
  canDrag: PropTypes.func,
  canDrop: PropTypes.func,
};

DnDItem.defaultProps = {
  className: "",
  displayType: DisplayTypes.GRID,
  as: "div",
  disabled: false,
  selected: false,
};

export default DnDItem;
